<template>
  <div class="popular-viewers-container">
    <div class="viewers-content">
      <div class="viewers-header">
        <!-- タイトル -->
        <div class="viewers-title">{{ $t('historiesTopViewedUser') }}</div>
      </div>

      <!-- メインビューワー -->
      <div class="main-viewer">
        <div class="main-viewer-avatar">
          <img 
            :src="mainViewer.avatar" 
            :alt="mainViewer.name" 
            class="main-avatar-img"
          />
        </div>
        <div class="main-viewer-info">
          <div class="main-viewer-time">{{ mainViewer.time }}</div>
          <div class="main-viewer-name">{{ mainViewer.name }}</div>
        </div>
      </div>

      <!-- サブビューワーリスト -->
      <div class="sub-viewers-list">
        <div 
          v-for="(viewer, index) in subViewers" 
          :key="index" 
          class="sub-viewer-item"
        >
          <div class="sub-viewer-avatar">
            <img 
              :src="viewer.avatar" 
              :alt="viewer.name" 
              class="sub-avatar-img"
            />
          </div>
          <div class="sub-viewer-info">
            <div class="sub-viewer-time">{{ viewer.time }}</div>
            <div class="sub-viewer-name">{{ viewer.name }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import { HistoryAlbumKeyedData } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumKeyedData'
import { HistoryAlbumUser } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumUser'

interface UserWithViewTime {
  avatar: string
  time: string
  name: string
}

@Component({})
export default class PopularUsers extends Vue {
  @Prop({ type: Number, required: true })
  year!: number

  // 年別データをpropsで受け取る
  @Prop({ type: Object, default: null })
  yearData!: HistoryAlbumKeyedData | null

  // ユーザー情報をpropsで受け取る
  @Prop({ type: Object, default: null })
  user!: HistoryAlbumUser | null

  // メインビューワー（実際のデータから取得）
  get mainViewer(): UserWithViewTime {
    if (!this.yearData || !this.yearData.broadcastViewRecords || this.yearData.broadcastViewRecords.length === 0) {
      return {
        avatar: '',
        time: String(this.$t('durationFormatHour', { hour: '0' })),
        name: ''
      }
    }

    // データはすでにソートされているので最初の要素を取得
    const topViewer = this.yearData.broadcastViewRecords
      .filter(record => record.streamer)[0] // streamerが存在する最初の要素

    if (!topViewer) {
      return {
        avatar: '',
        time: String(this.$t('durationFormatHour', { hour: '0' })),
        name: ''
      }
    }

    return {
      avatar: topViewer.streamer!.iconUrl,
      time: String(this.$t('durationFormatHour', { hour: topViewer.totalViewHour.toLocaleString() })),
      name: topViewer.streamer!.nickname
    }
  }

  // サブビューワーリスト（実際のデータから取得）
  get subViewers(): UserWithViewTime[] {
    if (!this.yearData || !this.yearData.broadcastViewRecords || this.yearData.broadcastViewRecords.length <= 1) {
      return []
    }

    // データはすでにソートされているので2位以降を取得（最大2人）
    return this.yearData.broadcastViewRecords
      .filter(record => record.streamer) // streamerが存在するもののみフィルタ
      .slice(1, 3) // 2位と3位を取得
      .map(record => ({
        avatar: record.streamer!.iconUrl,
        time: String(this.$t('durationFormatHour', { hour: record.totalViewHour.toLocaleString() })),
        name: record.streamer!.nickname
      }))
  }
}
</script>

<style scoped>
.popular-viewers-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  background: linear-gradient(117deg, #4E95FF 0%, #6DF2FF 100%);
  overflow: hidden;
}

/* 背景のVIEWテキスト */
.popular-viewers-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 220px;
  height: 50px;
  background-image: url('/img/campaign/reality_histories_20m/img_text_view.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: left top;
  opacity: 1;
  z-index: 1;
}

/* 背景のVIEWアイコン */
.popular-viewers-container::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: 120px;
  background-image: url('/img/campaign/reality_histories_20m/img_view.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right top;
  opacity: 1;
  z-index: 1;
}

.viewers-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 32px 16px 16px 16px;
  position: relative;
  width: 100%;
  z-index: 2;
}

/* ヘッダー */
.viewers-header {
  position: relative;
  width: 100%;
}

.viewers-title {
  font-weight: 600;
  font-style: normal;
  font-size: 26px;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  line-height: normal;
  position: relative;
  z-index: 3;
}

/* メインビューワー */
.main-viewer {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
}

.main-viewer-avatar {
  background: #ffffff;
  border: 1px solid #c5d1ff;
  border-radius: 99px;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-avatar-img {
  border-radius: 99px;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.main-viewer-info {
  flex: 1;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: 0;
  font-style: normal;
  padding: 0;
  color: #ffffff;
  text-align: left;
}

.main-viewer-time {
  font-weight: 400;
  position: relative;
  flex-shrink: 0;
  font-size: 16px;
  width: 100%;
  line-height: normal;
}

.main-viewer-name {
  font-weight: 600;
  position: relative;
  flex-shrink: 0;
  font-size: 20px;
  width: 100%;
  line-height: 24px;
}

/* サブビューワーリスト */
.sub-viewers-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
}

.sub-viewer-item {
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
  height: 44px;
}

.sub-viewer-avatar {
  border-radius: 99px;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sub-avatar-img {
  border-radius: 99px;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sub-viewer-info {
  flex: 1;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: 0;
  font-style: normal;
  padding: 0;
  color: #ffffff;
  text-align: left;
}

.sub-viewer-time {
  display: flex;
  flex-direction: column;
  font-weight: 300;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  font-size: 12px;
  width: 100%;
  line-height: normal;
}

.sub-viewer-name {
  font-weight: 600;
  position: relative;
  flex-shrink: 0;
  font-size: 14px;
  width: 100%;
  line-height: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
</style>
